import { Helmet } from 'react-helmet-async'
import { useState, useEffect } from 'react'
import { db } from '../lib/supabase'
import LoadingSpinner from '../components/LoadingSpinner'

export default function Legal() {
  const [contenidos, setContenidos] = useState([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState(null)
  const [seoConfig, setSeoConfig] = useState(null)
  const [faqs, setFaqs] = useState([])

  useEffect(() => {
    fetchContenidosLegales()
    loadSEOConfig()
    loadFAQs()
  }, [])

  const loadSEOConfig = async () => {
    const { data } = await db.getSEOConfig('legal')
    setSeoConfig(data || null)
  }

  const loadFAQs = async () => {
    const { data } = await db.getFAQsByScope('page', null, 'legal')
    setFaqs(data || [])
  }

  const fetchContenidosLegales = async () => {
    try {
      setLoading(true)
      // Usar db.getContenidoLegal() que filtra por activo = true Y publicado = true
      const { data, error } = await db.getContenidoLegal()

      if (error) throw error
      setContenidos(data || [])
    } catch (err) {
      console.error('Error fetching legal content:', err)
      setError('No se pudo cargar el contenido legal')
    } finally {
      setLoading(false)
    }
  }

  const getTipoIcon = (tipo) => {
    const icons = {
      'privacidad': '🔒',
      'terminos': '📜',
      'cookies': '🍪'
    }
    return icons[tipo] || '📄'
  }

  if (loading) {
    return (
      <>
        <Helmet>
          <title>Legal - Plaza Aurora</title>
        </Helmet>
        <div style={{
          maxWidth: 'var(--container-max-width)',
          margin: '0 auto',
          padding: '0 2rem'
        }}>
          <div style={{
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
            padding: '3rem'
          }}>
            <LoadingSpinner message="" size={64} />
          </div>
        </div>
      </>
    )
  }

  if (error) {
    return (
      <>
        <Helmet>
          <title>Legal - Plaza Aurora</title>
        </Helmet>
        <div style={{
          maxWidth: 'var(--container-max-width)',
          margin: '0 auto',
          padding: '0 2rem'
        }}>
          <div style={{
            background: 'linear-gradient(135deg, #FEE2E2 0%, #FECACA 100%)',
            border: '1px solid #F87171',
            borderRadius: 'var(--radius-md)',
            padding: '2rem',
            textAlign: 'center'
          }}>
            <h2 style={{
              fontSize: 'var(--font-size-h2)',
              fontWeight: '700',
              fontFamily: 'var(--font-display)',
              color: '#991B1B',
              marginBottom: '0.5rem'
            }}>Error</h2>
            <p style={{
              fontSize: 'var(--font-size-body)',
              fontFamily: 'var(--font-ui)',
              color: '#991B1B'
            }}>{error}</p>
          </div>
        </div>
      </>
    )
  }

  return (
    <>
      <Helmet>
        <title>{seoConfig?.meta_title || 'Legal - Plaza Aurora'}</title>
        <meta name="description" content={seoConfig?.meta_description || 'Información legal de Plaza Aurora Outlet - Políticas de privacidad, términos y condiciones'} />
        <link rel="canonical" href={seoConfig?.canonical_url || `${import.meta.env.VITE_SITE_URL || 'https://plazaaurora.pe'}/legal`} />
        
        {/* Open Graph */}
        <meta property="og:type" content="website" />
        <meta property="og:locale" content="es_PE" />
        <meta property="og:site_name" content="Plaza Aurora" />
        <meta property="og:url" content={seoConfig?.canonical_url || `${import.meta.env.VITE_SITE_URL || 'https://plazaaurora.pe'}/legal`} />
        <meta property="og:title" content={seoConfig?.og_title || seoConfig?.meta_title || 'Legal - Plaza Aurora'} />
        <meta property="og:description" content={seoConfig?.og_description || seoConfig?.meta_description || 'Información legal de Plaza Aurora Outlet - Políticas de privacidad, términos y condiciones'} />
        {seoConfig?.og_image_url && <meta property="og:image" content={seoConfig.og_image_url} />}
        
        {/* Twitter Card */}
        <meta name="twitter:card" content={seoConfig?.twitter_card || 'summary_large_image'} />
        <meta name="twitter:title" content={seoConfig?.og_title || seoConfig?.meta_title || 'Legal - Plaza Aurora'} />
        <meta name="twitter:description" content={seoConfig?.og_description || seoConfig?.meta_description || 'Información legal de Plaza Aurora Outlet - Políticas de privacidad, términos y condiciones'} />
        {seoConfig?.og_image_url && <meta name="twitter:image" content={seoConfig.og_image_url} />}
        
        {/* Robots */}
        <meta name="robots" content={seoConfig?.robots_index === false ? 'noindex,nofollow' : 'index,follow'} />
      </Helmet>
      
      {seoConfig?.json_ld && (
        <script type="application/ld+json">
          {seoConfig.json_ld}
        </script>
      )}
      
      <div style={{
        maxWidth: 'var(--container-max-width)',
        margin: '0 auto',
        padding: '4rem 2rem'
      }}>
        <h1 style={{
          fontSize: 'var(--font-size-h1)',
          fontWeight: '700',
          fontFamily: 'var(--font-display)',
          color: 'var(--color-text-primary)',
          marginBottom: '2rem'
        }}>Información Legal</h1>
        
        {contenidos.length === 0 ? (
          <div style={{
            background: 'var(--background)',
            borderRadius: 'var(--radius-md)',
            padding: '3rem',
            textAlign: 'center'
          }}>
            <p style={{
              fontSize: 'var(--font-size-body)',
              fontFamily: 'var(--font-ui)',
              color: 'var(--color-text-muted)'
            }}>No hay contenido legal disponible en este momento.</p>
          </div>
        ) : (
          <div style={{
            display: 'flex',
            flexDirection: 'column',
            gap: '3rem'
          }}>
            {contenidos.map((contenido) => (
              <div key={contenido.id} style={{
                background: 'var(--surface)',
                borderRadius: 'var(--radius-md)',
                boxShadow: 'none',
                border: '1px solid var(--mutedStroke)',
                padding: '2rem'
              }}>
                {/* Header */}
                <div style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: '0.75rem',
                  marginBottom: '1.5rem'
                }}>
                  <span style={{ fontSize: '2rem' }}>{getTipoIcon(contenido.tipo)}</span>
                  <h2 style={{
                    fontSize: 'var(--font-size-h2)',
                    fontWeight: '700',
                    fontFamily: 'var(--font-display)',
                    color: 'var(--color-text-primary)',
                    margin: 0
                  }}>{contenido.titulo}</h2>
                </div>
                
                {/* Content */}
                <div style={{
                  fontSize: 'var(--font-size-body)',
                  fontFamily: 'var(--font-ui)',
                  color: 'var(--color-text-primary)',
                  lineHeight: '1.7',
                  whiteSpace: 'pre-wrap'
                }}>
                  {contenido.contenido}
                </div>
                
                {/* Version Footer */}
                <div style={{
                  marginTop: '1.5rem',
                  paddingTop: '1.5rem',
                  borderTop: '1px solid var(--mutedStroke)'
                }}>
                  <p style={{
                    fontSize: 'var(--font-size-small)',
                    fontFamily: 'var(--font-ui)',
                    color: 'var(--color-text-muted)'
                  }}>
                    Versión {contenido.version} - Última actualización: {new Date(contenido.updated_at).toLocaleDateString('es-PE', { 
                      year: 'numeric', 
                      month: 'long', 
                      day: 'numeric' 
                    })}
                  </p>
                </div>
              </div>
            ))}
          </div>
        )}

        {/* FAQs Section */}
        {faqs.length > 0 && (
          <section id="faqs" role="region" aria-label="Preguntas Frecuentes" style={{ marginTop: '3rem' }}>
            <h2 style={{
              fontFamily: 'var(--font-display)',
              fontSize: 'var(--font-size-display-l)',
              fontWeight: 'var(--font-weight-bold)',
              color: 'var(--color-text-primary)',
              textAlign: 'center',
              marginBottom: '2rem'
            }}>Preguntas Frecuentes</h2>
            
            <div style={{
              maxWidth: '800px',
              margin: '0 auto',
              display: 'flex',
              flexDirection: 'column',
              gap: '1rem'
            }}>
              {faqs.map((faq) => (
                <details key={faq.id} style={{
                  background: 'var(--color-surface)',
                  borderRadius: 'var(--radius-lg)',
                  padding: '1.5rem',
                  border: '1px solid var(--color-stroke)',
                  cursor: 'pointer'
                }}>
                  <summary style={{
                    fontFamily: 'var(--font-ui)',
                    fontSize: 'var(--font-size-h4)',
                    fontWeight: 'var(--font-weight-semibold)',
                    color: 'var(--color-text-primary)',
                    listStyle: 'none',
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center'
                  }}>
                    {faq.pregunta}
                    <span style={{ fontSize: '1.5rem', marginLeft: '1rem' }}>+</span>
                  </summary>
                  <p style={{
                    fontFamily: 'var(--font-ui)',
                    fontSize: 'var(--font-size-body)',
                    color: 'var(--color-text-muted)',
                    lineHeight: '1.6',
                    marginTop: '1rem',
                    whiteSpace: 'pre-line'
                  }}>
                    {faq.respuesta}
                  </p>
                </details>
              ))}
            </div>
            
            {/* FAQPage JSON-LD */}
            <script type="application/ld+json">
              {JSON.stringify({
                "@context": "https://schema.org",
                "@type": "FAQPage",
                "mainEntity": faqs.map(faq => ({
                  "@type": "Question",
                  "name": faq.pregunta,
                  "acceptedAnswer": {
                    "@type": "Answer",
                    "text": faq.respuesta
                  }
                }))
              })}
            </script>
          </section>
        )}
      </div>
    </>
  )
}

